import { Accordion, AccordionDetails, AccordionSummary, accordionSummaryClasses, Avatar, Box, Button, Chip, Divider, IconButton, List, ListItemAvatar, ListItemButton, ListItemIcon, ListItemText, Radio, Tab, Tabs, tabsClasses, Typography, useMediaQuery, useTheme } from '@mui/material';
import { TuneOutlined, ShoppingBasketOutlined, ExpandMore } from '@mui/icons-material';
import { useAtom } from 'jotai';
import { SyntheticEvent, useEffect, useState } from 'react';
import { useRefinementList } from 'react-instantsearch';
import { useTranslation } from 'react-i18next';
import { Product_Sub_Family_Name_Enum } from '@kheops/graphql';
import useNormalizedCurrentRefinements from '../hooks/useNormalizedCurrentRefinements';
import { PackagingFamilyTypeIntl } from '../common/i18n/product-family.translation';
import { productSubFamilyImageMap } from '../discovery/state';
import Sheet from '../common/components/Sheet';
import { FeatureFlagKey, isFeatureEnabled } from '../common/utils/featureFlags';
import { isFilterDrawerOpenAtom } from './state';

export default function CompanyProductSubFamiliesFilter(): React.JSX.Element {
  const { t } = useTranslation(['product-family', 'settings', 'common']);
  const theme = useTheme();
  const isDownLg = useMediaQuery(theme.breakpoints.down('lg'));
  const [drawerOpen, setDrawerOpen] = useAtom(isFilterDrawerOpenAtom);
  const currentRefinements = useNormalizedCurrentRefinements('family') as string[];
  const [filterValue, setFilterValue] = useState<string | null>(currentRefinements[0] || null);
  const [draftValue, setDraftValue] = useState<string | null>(currentRefinements[0] || 'ALL');
  const { items, refine } = useRefinementList({ limit: 100, attribute: 'family' });
  const sortedProductSubFamiliesOptions = items.sort((itemA, itemB) => itemB.count - itemA.count || itemA.value.localeCompare(itemB.value));
  const productsCount = sortedProductSubFamiliesOptions.reduce((acc, item) => item.count + acc, 0);
  const classificationEnabled = isFeatureEnabled(FeatureFlagKey.PRODUCT_CLASSIFICATION);

  const handleTabsChange = (_: SyntheticEvent, value: string | null): void => {
    setFilterValue(value);
    setDraftValue(value);

    if (filterValue) {
      refine(filterValue);
    }

    if (value) {
      refine(value);
    }
  };

  const applyFilters = (): void => {
    if (filterValue) {
      refine(filterValue);
    }

    if (draftValue && draftValue !== 'ALL') {
      refine(draftValue);
    }

    setFilterValue(draftValue === 'ALL' ? null : draftValue);
    setDrawerOpen(false);
  };

  // If a value is preset (by the history navigation or by the packaging breadcrumb) we want to have the filter selected
  useEffect((): void => {
    if (!filterValue && currentRefinements.length) {
      setFilterValue(currentRefinements[0]);
      setDraftValue(currentRefinements[0]);
    }
  }, [currentRefinements]);

  return isDownLg && !classificationEnabled ? (
    <>
      <IconButton
        sx={{
          color: 'onSurfaceVariant.main',
        }}
        onClick={() => setDrawerOpen(true)}
        size="large"
      >
        <TuneOutlined />
      </IconButton>
      <Sheet
        open={drawerOpen}
        width={400}
        onClose={() => setDrawerOpen(false)}
        title={t('common:filters')}
        titleIcon={<TuneOutlined />}
      >
        <Box
          sx={{
            height: '100%',
            mt: 1.5,
          }}
        >
          <Accordion
            defaultExpanded
            disableGutters
            elevation={0}
            sx={{ background: 'transparent', gap: 2 }}
          >
            <AccordionSummary
              expandIcon={<ExpandMore />}
              sx={{
                [`& .${accordionSummaryClasses.content}`]: {
                  alignItems: 'center',
                  gap: 2,
                  m: 0,
                },
                px: 2,
                py: 1,
              }}
            >
              <ShoppingBasketOutlined fontSize="medium" />
              <Typography variant="bodyLarge">Rayons</Typography>
            </AccordionSummary>

            <AccordionDetails sx={{ px: 0, py: 0 }}>
              <List>
                <ListItemButton
                  onClick={() => setDraftValue('ALL')}
                >
                  <ListItemText primary={t('settings:all_departments')} />
                  <ListItemIcon>
                    <Radio
                      edge="start"
                      checked={draftValue === 'ALL'}
                    />
                  </ListItemIcon>
                </ListItemButton>
                {sortedProductSubFamiliesOptions.map((item) => (
                  <ListItemButton
                    key={item.value}
                    onClick={() => setDraftValue(item.value)}
                  >
                    <ListItemAvatar sx={{ minWidth: 40, height: 40 }}>
                      <Avatar size={40} src={productSubFamilyImageMap[item.value as Product_Sub_Family_Name_Enum]} />
                    </ListItemAvatar>
                    <ListItemText
                      primary={`${t(`product-family:${PackagingFamilyTypeIntl[item.value as Product_Sub_Family_Name_Enum]}`)} (${item.count})`}
                    />
                    <ListItemIcon>
                      <Radio
                        edge="start"
                        checked={draftValue === item.value}
                      />
                    </ListItemIcon>
                  </ListItemButton>
                ))}
              </List>
            </AccordionDetails>
          </Accordion>
        </Box>

        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            mt: 2,
            mb: 1,
            gap: 1,
          }}
        >
          <Button
            variant="text"
            color="inherit"
            onClick={() => setDrawerOpen(false)}
          >
            {t('common:cancel')}
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={() => applyFilters()}
          >
            {t('common:validate')}
          </Button>
        </Box>
      </Sheet>
    </>
  )
    : (
      <Tabs
        variant="scrollable"
        value={filterValue}
        sx={{
          [`& .${tabsClasses.list}`]: {
            gap: 1,
          },
          '& .MuiTab-root': {
            padding: 0,
          },
          [`.${tabsClasses.scrollButtons}`]: {
            display: {
              xs: 'none',
              md: 'block',
            },
          },
        }}
        onChange={handleTabsChange}
        slotProps={{
          indicator: {
            sx: {
              display: 'none',
            },
          }
        }}
      >
        <Tab
          disableRipple
          label={(
            <Chip
              variant="filter"
              label={`${t('common:all_products')} (${productsCount})`}
              className={filterValue === null ? 'Kheops-selected' : ''}
              sx={{ borderRadius: 10 }}
            />
          )}
          value={null}
        />
        <Divider flexItem orientation="vertical" sx={{ my: 1.5 }} />
        {sortedProductSubFamiliesOptions.map((item) => (
          <Tab
            key={item.value}
            disableRipple
            value={item.value}
            label={(
              <Chip
                variant="filter"
                avatar={(
                  <Box
                    component="img"
                    src={productSubFamilyImageMap[item.value as Product_Sub_Family_Name_Enum]}
                    sx={{
                      objectFit: 'cover',
                    }}
                  />
                )}
                label={`${t(`product-family:${PackagingFamilyTypeIntl[item.value as Product_Sub_Family_Name_Enum]}`)} (${item.count})`}
                className={filterValue === item.value ? 'Kheops-selected' : ''}
              />
            )}
          />
        ))}
      </Tabs>
    );
}
